// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify";
import submitContactUsForm from "@keller/App/Handlers/ContactUsForm/SubmitContactUsForm";
import { xMask } from "@keller/XDL/Utilities";
import { xScreen } from "@keller/XDL/Utilities";
import { maxLengthAttr, xValidation } from "@keller/XDL/Utilities/validation";
import React, { FC, RefObject, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    submitContactUsForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

interface OwnProps {
    location?: LocationModel | DealerModel;
    onSuccessSubmit?: () => void;
    onErrorSubmit?: () => void;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

interface ContactUsFormStyles {
    fieldWrapperStyle?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    errorMessageWrapper?: InjectableCss;
    selectFieldsWrapperStyle?: InjectableCss;
}

const contactUsFormStyles: ContactUsFormStyles = {
    fieldWrapperStyle: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    submitButton: {
        variant: "primary",
    },
    errorMessageWrapper: {
        css: css`
            margin-top: 10px;
            & > span {
                margin-bottom: 0;
            }
        `,
    },
    selectFieldsWrapperStyle: {
        css: css`
            padding-bottom: 15px;
            display: flex;
            gap: 28px;
            justify-content: space-between;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                gap: 15px;
            }
        `,
    },
};

const styles = contactUsFormStyles;
const StyledForm = getStyledWrapper("form");

const ContactUsForm: FC<Props> = ({
    submitContactUsForm,
    validateReCaptcha,
    location,
    onSuccessSubmit,
    onErrorSubmit,
}) => {
    const emailRequiredFieldMessage = siteMessage("ContactUsForm_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("ContactUsForm_EmailIsInvalidErrorMessage");
    const phoneFieldMessage = siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
    const phoneRequiredFieldMessage = siteMessage("ContactUsForm_PhoneNumberIsRequiredErrorMessage");
    const nameRequiredFieldMessage = siteMessage("Keller_ContactUsForm_NameRequired");
    const contactByRequiredFieldMessage = siteMessage("Keller_ContactUsForm_ContactByRequired");
    const messageRequiredFieldMessage = siteMessage("Keller_ContactUsForm_MessageRequired");

    const toaster = useToaster();

    const [errorMessage, setErrorMessage] = useState("");
    const [name, setName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [phone, setPhone] = React.useState("");
    const [message, setMessage] = React.useState("");
    const [contactBy, setContactByDropDown] = React.useState("");
    const [emailContact, setEmailContactDropDown] = React.useState("");

    const [nameError, setNameError] = useState(nameRequiredFieldMessage);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [phoneError, setPhoneError] = useState(phoneRequiredFieldMessage);
    const [messageError, setMessageError] = useState(messageRequiredFieldMessage);

    const [contactByDropDownError, setContactByDropDownError] = useState(contactByRequiredFieldMessage);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isShowroomEmail, setIsShowroomEmail] = React.useState(false);
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : xValidation.email(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validatePhoneNumber = (phoneNumber: string) => {
        const errorMessage = !phoneNumber
            ? phoneRequiredFieldMessage
            : xValidation.phone(phoneNumber)
              ? ""
              : phoneFieldMessage;
        setPhoneError(errorMessage);
        return !errorMessage;
    };

    const nameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
        setNameError(!event.target.value.trim() ? nameRequiredFieldMessage : "");
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        validateEmail(event.target.value);
        setEmail(event.target.value);
    };

    const phoneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        validatePhoneNumber(event.target.value);
        setPhone(event.target.value);
    };

    const handleContactByDropDownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setContactByDropDown(event.currentTarget.value);
        setContactByDropDownError(!event.target.value.trim() ? contactByRequiredFieldMessage : "");
    };

    const handleEmailContactSelection = (event: React.ChangeEvent<HTMLSelectElement>) => {
        event.preventDefault();
        setEmailContactDropDown(event.currentTarget.value);
        event.currentTarget.value.toLowerCase() === "showroom" ? setIsShowroomEmail(true) : setIsShowroomEmail(false);
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
        setMessageError(!event.target.value.trim() ? messageRequiredFieldMessage : "");
    };

    const validateForm = () => {
        return !(phoneError || nameError || emailError || messageError || contactByDropDownError);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateForm()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUsForm", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        submitContactUsForm({
            name,
            emailAddress: email,
            phone,
            contactMeBy: contactBy,
            message,
            locationName: location ? location.name : "",
            useShowroomEmail: isShowroomEmail,
            onSuccess: () => {
                setIsSubmitting(false);
                setIsSubmitted(false);
                toaster.addToast({
                    body: siteMessage("Keller_ContactUsForm_SuccessMessage"),
                    messageType: "success",
                });
                resetFields();
                onSuccessSubmit?.();
            },
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
                onErrorSubmit?.();
            },
        });
    };

    const resetFields = () => {
        setName("");
        setNameError(nameRequiredFieldMessage);

        setEmail("");
        setEmailError(emailRequiredFieldMessage);

        setPhone("");
        setPhoneError(phoneRequiredFieldMessage);

        setMessage("");
        setMessageError(messageRequiredFieldMessage);

        setContactByDropDown("");
        setContactByDropDownError(contactByRequiredFieldMessage);

        setEmailContactDropDown("");
        setIsShowroomEmail(false);
    };

    return (
        <LoadingOverlay loading={isSubmitting}>
            <StyledForm onSubmit={handleSubmit} noValidate>
                <StyledWrapper {...styles.fieldWrapperStyle}>
                    <TextField
                        placeholder={translate("Your Name")}
                        onChange={nameChangeHandler}
                        value={name}
                        error={isSubmitted && nameError}
                        maxLength={maxLengthAttr.userName}
                        required={true}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.fieldWrapperStyle}>
                    <TextField
                        type="email"
                        placeholder={translate("Your Email")}
                        onChange={emailChangeHandler}
                        value={email}
                        error={isSubmitted && emailError}
                        maxLength={maxLengthAttr.email}
                        required={true}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.fieldWrapperStyle}>
                    <TextField
                        required
                        placeholder={translate("Your Phone")}
                        onChange={phoneChangeHandler}
                        value={xMask.phoneNumberUS(phone)}
                        error={isSubmitted && phoneError}
                        maxLength={maxLengthAttr.phone}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.selectFieldsWrapperStyle}>
                    <Select
                        label={translate("Contact Me By")}
                        required={true}
                        value={contactBy}
                        onChange={handleContactByDropDownChange}
                        error={isSubmitted && contactByDropDownError}
                    >
                        <option value="">{translate("Contact Me By")}</option>
                        <option value="Email">{translate("Email")}</option>
                        <option value="Phone">{translate("Phone")}</option>
                    </Select>
                    {(location as DealerModel)?.properties?.showroomEmail && (
                        <Select
                            label={translate("Choose Email Contact")}
                            value={emailContact}
                            onChange={handleEmailContactSelection}
                            error={isSubmitted}
                        >
                            <option value="Branch">{translate("Branch Email")}</option>
                            <option value="Showroom">{translate("Showroom Email")}</option>
                        </Select>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.fieldWrapperStyle}>
                    <TextArea
                        placeholder={translate("Message")}
                        onChange={messageChangeHandler}
                        value={message}
                        error={isSubmitted && messageError}
                    />
                </StyledWrapper>
                <ReCaptcha location="ContactUs" recaptchaRef={recaptchaRef} />
                <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                    {translate("Send Message")}
                </Button>
                {isSubmitted && errorMessage && (
                    <StyledWrapper {...styles.errorMessageWrapper}>
                        <Notify type="danger">{errorMessage}</Notify>
                    </StyledWrapper>
                )}
            </StyledForm>
        </LoadingOverlay>
    );
};

export default connect(null, mapDispatchToProps)(ContactUsForm);
